import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileSignature, ShieldCheck, Stethoscope, Loader2, Settings2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClinic } from "@/store/ClinicContext";
import { useAuth } from "@/store/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("veloclin_token")}`, "Content-Type": "application/json" });

const MemedConfigForm = ({ onSaved }) => {
  const { currentUser } = useAuth();
  const [form, setForm] = useState({
    sobrenome: "", cpf: "", data_nascimento: "", sexo: "M",
    board_number: currentUser?.crm || "", board_state: currentUser?.uf || "",
    cidade_id: "", especialidade_id: "",
  });
  const [especialidades, setEspecialidades] = useState([]);
  const [cidadeQuery, setCidadeQuery] = useState("");
  const [cidadeResults, setCidadeResults] = useState([]);
  const [cidadeLabel, setCidadeLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/memed/catalog/especialidades`, { headers: authHeaders() })
      .then((r) => r.json()).then((d) => setEspecialidades(d.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (cidadeQuery.trim().length < 3) { setCidadeResults([]); return; }
    const t = setTimeout(() => {
      fetch(`${API}/api/memed/catalog/cidades?q=${encodeURIComponent(cidadeQuery)}`, { headers: authHeaders() })
        .then((r) => r.json()).then((d) => setCidadeResults(d.items || [])).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [cidadeQuery]);

  const save = async () => {
    if (!form.sobrenome.trim()) return toast.error("Informe o sobrenome.");
    if (form.cpf.replace(/\D/g, "").length < 11) return toast.error("Informe um CPF válido.");
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(form.data_nascimento)) return toast.error("Data de nascimento no formato DD/MM/AAAA.");
    if (!form.cidade_id) return toast.error("Selecione a cidade.");
    if (!form.especialidade_id) return toast.error("Selecione a especialidade.");
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/memed/prescriber`, { method: "POST", headers: authHeaders(), body: JSON.stringify(form) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao cadastrar prescritor.");
      toast.success("Prescritor cadastrado na Memed!", { description: "Você já pode emitir prescrições e exames." });
      onSaved(d);
    } catch (e) {
      toast.error("Falha ao cadastrar na Memed", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="memed-config-form" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Settings2 className="h-5 w-5" /></div>
        <div>
          <h3 className="font-heading text-lg font-bold text-slate-900">Configurar Prescrição (Memed)</h3>
          <p className="text-sm text-slate-500">Preencha uma vez para habilitar a emissão de receitas e exames digitais.</p>
        </div>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-semibold text-slate-600">Sobrenome</Label><Input data-testid="memed-sobrenome-input" value={form.sobrenome} onChange={(e) => setForm({ ...form, sobrenome: e.target.value })} className="mt-1.5" placeholder="Silva" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">CPF</Label><Input data-testid="memed-cpf-input" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} className="mt-1.5" placeholder="000.000.000-00" /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs font-semibold text-slate-600">Nascimento</Label><Input data-testid="memed-birth-input" value={form.data_nascimento} onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} className="mt-1.5" placeholder="DD/MM/AAAA" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Sexo</Label>
            <Select value={form.sexo} onValueChange={(v) => setForm({ ...form, sexo: v })}>
              <SelectTrigger data-testid="memed-sexo-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="M">Masculino</SelectItem><SelectItem value="F">Feminino</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-semibold text-slate-600">CRM · UF</Label>
            <div className="mt-1.5 flex gap-1.5">
              <Input data-testid="memed-crm-input" value={form.board_number} onChange={(e) => setForm({ ...form, board_number: e.target.value })} placeholder="CRM" />
              <Input data-testid="memed-uf-input" value={form.board_state} onChange={(e) => setForm({ ...form, board_state: e.target.value.toUpperCase().slice(0, 2) })} className="w-16" placeholder="UF" />
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Especialidade</Label>
          <Select value={form.especialidade_id} onValueChange={(v) => setForm({ ...form, especialidade_id: v })}>
            <SelectTrigger data-testid="memed-especialidade-select" className="mt-1.5"><SelectValue placeholder="Selecione a especialidade" /></SelectTrigger>
            <SelectContent className="max-h-64">{especialidades.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Label className="text-xs font-semibold text-slate-600">Cidade</Label>
          {cidadeLabel ? (
            <div className="mt-1.5 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
              <span className="font-semibold text-slate-700">{cidadeLabel}</span>
              <button data-testid="memed-cidade-clear" onClick={() => { setCidadeLabel(""); setForm({ ...form, cidade_id: "" }); }} className="text-xs font-semibold text-blue-600 hover:underline">Trocar</button>
            </div>
          ) : (
            <>
              <div className="relative mt-1.5">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="memed-cidade-input" value={cidadeQuery} onChange={(e) => setCidadeQuery(e.target.value)} className="pl-9" placeholder="Digite ao menos 3 letras..." />
              </div>
              {cidadeResults.length > 0 && (
                <div data-testid="memed-cidade-results" className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                  {cidadeResults.map((c) => (
                    <button key={c.id} data-testid={`memed-cidade-opt-${c.id}`} onClick={() => { setForm({ ...form, cidade_id: c.id }); setCidadeLabel(`${c.nome} - ${c.uf}`); setCidadeResults([]); setCidadeQuery(""); }} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50">
                      <span>{c.nome}</span><span className="text-xs text-slate-400">{c.uf}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <button data-testid="memed-config-save" onClick={save} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:bg-slate-300">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          {saving ? "Cadastrando na Memed..." : "Cadastrar prescritor e habilitar"}
        </button>
      </div>
    </div>
  );
};

export const MemedEmbed = () => {
  const { patients } = useClinic();
  const [state, setState] = useState({ loading: true, configured: false, token: null, scriptUrl: "", error: null });
  const [ready, setReady] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const scriptLoadedFor = useRef(null);

  const fetchPrescriber = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/memed/prescriber`, { headers: authHeaders() });
      const d = await r.json();
      setState({ loading: false, configured: d.configured, token: d.token || null, scriptUrl: d.script_url || "", error: d.error || null });
    } catch (e) {
      setState({ loading: false, configured: false, token: null, scriptUrl: "", error: e.message });
    }
  }, []);

  useEffect(() => { fetchPrescriber(); }, [fetchPrescriber]);

  const onFinished = useCallback((event) => {
    const payload = event?.data ?? event ?? {};
    toast.success("Documento emitido na Memed", { description: "A Memed cuida do envio ao paciente (e-mail/compartilhamento)." });
    return payload;
  }, []);

  useEffect(() => {
    if (!state.configured || !state.token || !state.scriptUrl) return;
    if (scriptLoadedFor.current === state.token) return;
    scriptLoadedFor.current = state.token;
    const s = document.createElement("script");
    s.src = state.scriptUrl; s.async = true; s.dataset.token = state.token; s.id = "memed-sinapse-script";
    s.onload = () => {
      const iv = setInterval(() => {
        if (window.MdSinapsePrescricao && window.MdSinapsePrescricao.event) {
          clearInterval(iv);
          window.MdSinapsePrescricao.event.add("core:moduleInit", (module) => {
            if (module?.name === "plataforma.prescricao") {
              setReady(true);
              try {
                window.MdHub.event.add("prescricaoImpressa", onFinished);
                window.MdHub.event.add("prescricaoGerada", onFinished);
              } catch (e) { /* ignore */ }
            }
          });
        }
      }, 300);
      setTimeout(() => clearInterval(iv), 15000);
    };
    document.body.appendChild(s);
  }, [state.configured, state.token, state.scriptUrl, onFinished]);

  const openPrescricao = async () => {
    if (!ready || !window.MdHub) return toast.error("Módulo Memed ainda carregando. Tente em instantes.");
    const p = patients.find((x) => x.id === selectedId);
    if (!p) return toast.error("Selecione um paciente.");
    if (!(p.cpf || "").trim() || !(p.data_nascimento || "").trim()) {
      return toast.error("Paciente sem CPF ou data de nascimento", { description: "Edite o cadastro do paciente para incluir CPF e data de nascimento (exigidos pela Memed)." });
    }
    try {
      await window.MdHub.command.send("plataforma.prescricao", "setPaciente", {
        idExterno: p.id, nome: p.name,
        sexo: p.sexo === "F" ? "Feminino" : "Masculino",
        cpf: (p.cpf || "").replace(/\D/g, ""),
        data_nascimento: p.data_nascimento,
        telefone: (p.phone || "").replace(/\D/g, ""),
        email: p.email || "",
      });
      await window.MdHub.module.show("plataforma.prescricao");
    } catch (e) {
      toast.error("Falha ao abrir a Memed", { description: e.message });
    }
  };

  if (state.loading) {
    return <div data-testid="memed-loading" className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-16 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Carregando Memed...</div>;
  }

  if (!state.configured) {
    return <MemedConfigForm onSaved={(d) => { setState({ loading: false, configured: true, token: d.token, scriptUrl: d.script_url, error: null }); }} />;
  }

  return (
    <div data-testid="memed-embed" className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Stethoscope className="h-5 w-5" /></div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-heading text-lg font-bold text-slate-900">Prescrição Inteligente</h3>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Memed · ICP-Brasil</span>
            </div>
            <p className="mt-1 text-sm text-slate-500">Receitas e pedidos de exames com validade jurídica, assinados digitalmente e enviados ao paciente pela própria Memed.</p>
          </div>
        </div>

        {state.error && (
          <p data-testid="memed-error" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">Não foi possível obter o token do prescritor: {state.error}</p>
        )}

        <div className="mt-5">
          <Label className="text-xs font-semibold text-slate-600">Paciente</Label>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger data-testid="memed-patient-select" className="mt-1.5"><SelectValue placeholder={patients.length ? "Selecione o paciente" : "Nenhum paciente cadastrado"} /></SelectTrigger>
            <SelectContent className="max-h-64">{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{(!p.cpf || !p.data_nascimento) ? " (sem CPF/nascimento)" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <button data-testid="open-memed-btn" onClick={openPrescricao} disabled={!ready} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 text-base font-bold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:bg-slate-300">
          {ready ? <FileSignature className="h-5 w-5" /> : <Loader2 className="h-5 w-5 animate-spin" />}
          {ready ? "Abrir Receituário e Exames" : "Carregando módulo Memed..."}
        </button>
      </div>
    </div>
  );
};
